import { ApolloClient } from '@apollo/client';
import { createContext, useContext, useEffect } from 'react';

import useListComponentByRangeQuery, { refetchAllComponentPages } from './useListComponentByRangeQuery';
import useListPaginatedFoodsQuery, { refetchAllFoodPages } from './useListPaginatedFoodsQuery';
import { Kitchen } from '../libs';

/** Counts a screen's in-flight kitchen data load up (+1) or down (-1); App shows its progress bar while any are pending. */
export const DataLoadingContext = createContext<(delta: 1 | -1) => void>(() => undefined);

export const useReportDataLoading = (loading: boolean) => {
  const report = useContext(DataLoadingContext);
  useEffect(() => {
    if (!loading) return;
    report(1);
    return () => report(-1);
  }, [loading, report]);
};

// One set of variables per kitchen and day, so every screen reads the same cache entry and the data is fetched once.
const componentRange = (kitchen: string, date: string) => ({ kitchen, startDate: date, endDate: date });
const foodsDay = (kitchen: string, date: string) => ({ kitchen: kitchen as Kitchen, date });

export const useKitchenComponents = (kitchen: string, date: string) => {
  const result = useListComponentByRangeQuery({ ...componentRange(kitchen, date), fetchAll: true });
  useReportDataLoading(result.componentsLoading);
  return result;
};

export const useKitchenFoods = (kitchen: string, date: string) => {
  const result = useListPaginatedFoodsQuery({ ...foodsDay(kitchen, date), fetchAll: true });
  useReportDataLoading(result.foodsLoading);
  return result;
};

export const refreshLoadedKitchenComponents = (client: ApolloClient, kitchen: string, date: string) =>
  refetchAllComponentPages(client, componentRange(kitchen, date), { onlyIfLoaded: true });

export const refreshLoadedKitchenFoods = (client: ApolloClient, kitchen: string, date: string) =>
  refetchAllFoodPages(client, foodsDay(kitchen, date), { onlyIfLoaded: true });
